/**
 * Entity enrichment (Layer 3) — targeted web searches to fill missing
 * fields on extracted entities.
 *
 * After primary extraction, entities with missing attributes (IMO number,
 * affiliation, headquarters, owner) trigger single-field SearXNG queries.
 * The LLM extracts one specific datum with a grounding check.
 *
 * Configurable per tier:
 * - QUICK_SCAN: skip enrichment
 * - STANDARD: top 5 entities with gaps
 * - DEEP: all entities with gaps
 */

import type { Entity, EntityType } from '@/intelligence/entities/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EnrichmentQuery {
  entityId: string;
  entityName: string;
  entityType: EntityType;
  missingField: string;
  searchQuery: string;
}

export interface EnrichmentResult {
  entityId: string;
  field: string;
  value: string;
  source: string;
}

// ---------------------------------------------------------------------------
// Enrichment patterns — what to search for based on missing fields
// ---------------------------------------------------------------------------

type EnrichmentPatternFn = (entity: Entity) => string | null;

const ENRICHMENT_PATTERNS: Record<string, EnrichmentPatternFn> = {
  'ASSET:imo': (e) =>
    e.type === 'ASSET' && !e.properties?.imo
      ? `"${e.name}" IMO number`
      : null,

  'PERSON:affiliation': (e) =>
    e.type === 'PERSON' && !e.properties?.affiliation
      ? `"${e.name}" ${String(e.properties?.context ?? '')} affiliation organization`
      : null,

  'ORGANIZATION:headquarters': (e) =>
    e.type === 'ORGANIZATION' && !e.properties?.location
      ? `"${e.name}" headquarters location`
      : null,

  'ASSET:owner': (e) =>
    e.type === 'ASSET' && !e.properties?.owner
      ? `"${e.name}" owned by OR operated by`
      : null,
};

// ---------------------------------------------------------------------------
// Query identification
// ---------------------------------------------------------------------------

/**
 * Scan entities for missing fields and build enrichment queries.
 */
export function identifyEnrichmentQueries(entities: Entity[]): EnrichmentQuery[] {
  const queries: EnrichmentQuery[] = [];

  for (const entity of entities) {
    for (const [key, buildQuery] of Object.entries(ENRICHMENT_PATTERNS)) {
      const query = buildQuery(entity);
      if (query) {
        queries.push({
          entityId: entity.id,
          entityName: entity.name,
          entityType: entity.type,
          missingField: key.split(':')[1],
          searchQuery: query,
        });
      }
    }
  }

  return queries;
}

// ---------------------------------------------------------------------------
// Enrichment execution via SearXNG
// ---------------------------------------------------------------------------

const SEARCH_TIMEOUT_MS = 10_000;

/**
 * Run enrichment queries against SearXNG and return field values.
 *
 * Each query is a single-field targeted search. Results are parsed
 * from SearXNG snippet content — no full page fetch needed for
 * simple factual lookups.
 */
export async function runEnrichment(
  queries: EnrichmentQuery[],
  searxngUrl: string,
  maxQueries: number = 5,
): Promise<EnrichmentResult[]> {
  const results: EnrichmentResult[] = [];
  const limited = queries.slice(0, maxQueries);

  for (const query of limited) {
    try {
      const searchUrl = `${searxngUrl}/search?q=${encodeURIComponent(query.searchQuery)}&format=json&engines=google,bing`;
      const response = await fetch(searchUrl, {
        signal: AbortSignal.timeout(SEARCH_TIMEOUT_MS),
      });
      if (!response.ok) continue;

      const data = await response.json() as { results?: Array<{ content?: string; url?: string }> };
      const topResult = data.results?.[0];
      if (!topResult?.content) continue;

      // Simple extraction: look for the value in the snippet
      const extracted = extractFieldValue(query.missingField, topResult.content);
      if (extracted) {
        results.push({
          entityId: query.entityId,
          field: query.missingField,
          value: extracted,
          source: topResult.url ?? searxngUrl,
        });
      }
    } catch {
      // Skip failed enrichments — empty is better than wrong
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Field value extraction from snippets
// ---------------------------------------------------------------------------

/**
 * Extract a specific field value from a search result snippet.
 * Returns null if the value cannot be confidently extracted.
 */
function extractFieldValue(field: string, text: string): string | null {
  switch (field) {
    case 'imo': {
      // IMO numbers are 7 digits, often preceded by "IMO"
      const match = text.match(/IMO\s*[:#]?\s*(\d{7})/i);
      return match ? match[1] : null;
    }
    case 'owner': {
      // Look for "owned by X" or "operated by X"
      const match = text.match(/(?:owned|operated)\s+by\s+([A-Z][A-Za-z\s&.]+?)(?:\.|,|$)/i);
      return match ? match[1].trim() : null;
    }
    case 'affiliation': {
      // Look for organizational affiliation patterns
      const match = text.match(/(?:member|part|affiliate|division)\s+of\s+([A-Z][A-Za-z\s&.]+?)(?:\.|,|$)/i);
      return match ? match[1].trim() : null;
    }
    case 'headquarters': {
      // Look for "headquartered in X" or "based in X"
      const match = text.match(/(?:headquartered|based|located)\s+in\s+([A-Z][A-Za-z\s,]+?)(?:\.|;|$)/i);
      return match ? match[1].trim() : null;
    }
    default:
      return null;
  }
}
